import React from "react";
import { Dropdown } from "primereact/dropdown";
import { Plus } from "lucide-react";

const DynamicItemTable = ({
    columns = [],
    data = [],
    errors = [],
    onAdd,
    onRemove,
    onChange,
    onBlur,
    footer,
    canAddMore = true,
    isLoading = false,
    generalError = "",
    title = "Item Details",
    addButtonLabel = "Add Item",
    completeDetailsMessage = "Complete current item details to add more",
    // Auto button props
    showAutoButton = false,
    onAutoClick,
    autoButtonDisabled = false,
    autoButtonTitle = "Add Auto"
}) => {

    const renderField = (column, item, index) => {
        const { field, type, options, placeholder, readOnly, disabled, required, min, step } = column;
        const error = errors[index]?.[field];
        const value = item[field];

        // Evaluate disabled state - can be boolean or function
        const isDisabled = typeof disabled === 'function' ? disabled(item) : disabled;

        switch (type) {
            case "index":
                return <strong>{index + 1}</strong>;

            case "dropdown":
                return (
                    <div className="custom-dropdown">
                        <Dropdown
                            placeholder={placeholder || `Select ${column.label}`}
                            filter
                            value={value}
                            options={options || []}
                            onChange={(e) => onChange(index, field, e.value)}
                            filterBy="label"
                            style={{ width: column.width || "100%" }}
                            disabled={isDisabled || isLoading}
                        />
                        {error && <small className="text-danger">{error}</small>}
                    </div>
                );

            case "select":
                return (
                    <>
                        <select
                            className={`form-control form-control-sm ${error ? "is-invalid" : ""}`}
                            value={value || ""}
                            onChange={(e) => onChange(index, field, e.target.value)}
                            onBlur={() => onBlur && onBlur(index, field)}
                            disabled={isDisabled || isLoading}
                        >
                            <option value="">{placeholder || `Select ${column.label}`}</option>
                            {options?.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        {error && <small className="text-danger">{error}</small>}
                    </>
                );

            case "number":
                return (
                    <>
                        <input
                            type="number"
                            className={`form-control form-control-sm ${error ? "is-invalid" : ""}`}
                            value={value}
                            onChange={(e) => onChange(index, field, e.target.value)}
                            onBlur={() => onBlur && onBlur(index, field)}
                            placeholder={placeholder || "0"}
                            min={min}
                            step={step}
                            disabled={isDisabled || isLoading}
                        />
                        {error && <small className="text-danger">{error}</small>}
                    </>
                );

            case "text":
            case "readonly":
                return (
                    <>
                        <input
                            type="text"
                            className={`form-control ${(readOnly || type === "readonly") ? "bg-light" : ""} ${error ? "is-invalid" : ""}`}
                            value={value || ""}
                            onChange={(e) => onChange(index, field, e.target.value)}
                            onBlur={() => onBlur && onBlur(index, field)}
                            placeholder={placeholder || ""}
                            readOnly={readOnly || type === "readonly"}
                            disabled={isDisabled || isLoading}
                            style={{ width: "100%" }}
                        />
                        {error && <small className="text-danger">{error}</small>}
                    </>
                );

            case "action":
                return (
                    <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => onRemove(index, item)}
                        disabled={isLoading}
                        title="Delete Item"
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                );

            default:
                return value;
        }
    };

    return (
        <>
            <div className="row mt-3 bg-white px-2 pt-3 mx-1" style={{ borderTopLeftRadius: "10px", borderTopRightRadius: "10px" }}>
                <div className="col-12">
                    <div className="form-heading d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h4 className="mb-0">{title}</h4>
                        </div>
                        <div className="add-group gap-2">
                            {showAutoButton && (
                                <button
                                    type="button"
                                    className="btn w-100 rounded-3 btn-primary add-pluss gap-1 d-flex flex-row align-items-center"
                                    onClick={onAutoClick}
                                    disabled={autoButtonDisabled || isLoading}
                                    title={autoButtonTitle}
                                    style={{
                                        backgroundColor: "var(--primary-clr)",
                                        color: "white",
                                    }}
                                >
                                    Auto
                                    <Plus size={16} />
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn d-flex rounded-3 text-nowrap align-items-center add-pluss"
                                style={{
                                    backgroundColor: "var(--primary-clr)",
                                    color: "white",
                                }}
                                onClick={onAdd}
                                disabled={!canAddMore || isLoading}
                                title={!canAddMore ? completeDetailsMessage : addButtonLabel}
                            >
                                <i className="fa fa-plus me-1"></i> {addButtonLabel}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row bg-white mx-1" style={{ borderBottomRightRadius: "10px", borderBottomLeftRadius: "10px" }}>
                <div className="col-12">
                    <div className="table-responsive" style={{ minHeight: "100px" }}>
                        <table className="table table-bordered table-nowrap table-hover">
                            <thead className="table-light">
                                <tr>
                                    {columns.map((col, i) => (
                                        <th
                                            key={i}
                                            style={{
                                                width: col.width,
                                                minWidth: col.minWidth,
                                                textAlign: col.align || "left",
                                                verticalAlign: "middle"
                                            }}
                                            className={col.className}
                                        >
                                            {col.label} {col.required && <span className="text-danger">*</span>}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((item, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {columns.map((col, colIndex) => (
                                            <td
                                                key={colIndex}
                                                className={`align-middle ${col.type === "index" || col.type === "action" ? "text-center" : ""}`}
                                            >
                                                {renderField(col, item, rowIndex)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                            {footer && (
                                <tfoot>
                                    <tr style={{ background: "#f8f9fa", fontWeight: "600" }}>
                                        {footer.map((cell, i) => (
                                            <td
                                                key={i}
                                                colSpan={cell.colSpan || 1}
                                                className={`text-${cell.align || "start"}`}
                                            >
                                                {cell.label || cell.value || ""}
                                            </td>
                                        ))}
                                    </tr>
                                </tfoot>
                            )}
                        </table>

                        {/* {generalError && (
                            <div className="alert alert-danger mt-2 py-2">
                                <i className="fa fa-exclamation-circle me-2"></i>
                                {generalError}
                            </div>
                        )} */}

                        {!canAddMore && data.length > 0 && (
                            <div
                                className="alert mt-2 py-2"
                                style={{
                                    backgroundColor: "rgba(255, 105, 35, 0.1)",
                                    border: "1px solid var(--primary-clr)",
                                    color: "var(--primary-clr)",
                                    fontSize: "12px",
                                }}
                            >
                                <i className="fa fa-info-circle me-2"></i>
                                {completeDetailsMessage}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default DynamicItemTable;
